import type { BaseQueryFn, FetchArgs } from '@reduxjs/toolkit/query';
import { fetchBaseQuery } from '@reduxjs/toolkit/query';
import { Mutex } from 'async-mutex';
import { stringify } from 'qs';

import { EFeatureFlags, ENVS, HTTPStatusCode } from '@/shared/consts';
import {
  checkIsFlagEnabled,
  getCasdoorAccessToken,
  getCasdoorRefreshToken,
  getTeachbaseAccessToken,
  logoutUser,
  saveCasdoorTokens,
} from '@/shared/helpers';

import type { ICasdoorAuthDataResponse } from '../types';

const URLS_WITH_CASDOOR_TOKEN = ['/signin', '/auth_links'];
const ENDPOINT_WITH_CASDOOR_TOKEN = ['getTeachbaseAuthData', 'generateTeachbaseAuthLink'];

const mutex = new Mutex();

const teachbaseBaseQuery = ({ baseUrl }: { baseUrl: string }) =>
  fetchBaseQuery({
    baseUrl,
    paramsSerializer: (params) => {
      if (params instanceof URLSearchParams) {
        return params.toString();
      }

      return stringify(params, {
        arrayFormat: 'brackets',
        skipNulls: true,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        filter: (_, value) => value || undefined,
      });
    },
    prepareHeaders: (headers, { endpoint }) => {
      headers.set('Content-Type', 'application/json');
      headers.set('Accept-Language', 'ru');

      if (ENDPOINT_WITH_CASDOOR_TOKEN.includes(endpoint)) {
        const casdoorAccessToken = getCasdoorAccessToken();

        if (casdoorAccessToken) {
          headers.set('Authorization', `Bearer ${casdoorAccessToken}`);
        }

        return headers;
      }

      const teachbaseAccessToken = getTeachbaseAccessToken();

      if (teachbaseAccessToken) {
        headers.set('Authorization', `Bearer ${teachbaseAccessToken}`);
      }

      return headers;
    },
    credentials: checkIsFlagEnabled(EFeatureFlags.FetchCredentials) ? 'include' : 'same-origin',
  });

export const teachbaseBaseQueryWithReauth =
  ({ baseUrl }: { baseUrl: string }): BaseQueryFn =>
  async (args, api, extraOptions) => {
    const baseQuery = teachbaseBaseQuery({ baseUrl });

    const fetchArgs = args as string | FetchArgs;

    const refreshCasdoorTokens = async () => {
      const refreshToken = getCasdoorRefreshToken();

      if (!refreshToken) {
        logoutUser();

        return null;
      }

      const result = await baseQuery(
        {
          method: 'POST',
          url: `${ENVS.CASDOOR_API_URL}/login/oauth/refresh_token`,
          params: {
            grant_type: 'refresh_token',
            refresh_token: refreshToken,
            scope: 'openid+profile+email',
            client_id: ENVS.CASDOOR_CLIENT_ID,
          },
        },
        { ...api, endpoint: 'refresh_token' },
        extraOptions,
      );

      if (!result.data) {
        logoutUser();

        return null;
      }

      const data = result.data as ICasdoorAuthDataResponse;

      saveCasdoorTokens({
        accessToken: data.access_token,
        refreshToken: data.refresh_token,
      });

      return data;
    };

    const loginTeachbase = async () => {
      const result = await baseQuery(
        {
          method: 'POST',
          url: `${ENVS.LMS_URL}/signin`,
        },
        api,
        extraOptions,
      );

      if (!result.data) {
        logoutUser();

        return null;
      }

      return result.data;
    };

    await mutex.waitForUnlock();

    let result = await baseQuery(fetchArgs, api, extraOptions);

    if (result.error?.status !== HTTPStatusCode.UnAuthorized) {
      return result;
    }

    const url = typeof fetchArgs === 'string' ? fetchArgs : fetchArgs.url;

    const isCasdoorTokenRequest = URLS_WITH_CASDOOR_TOKEN.includes(url);

    if (mutex.isLocked()) {
      await mutex.waitForUnlock();
      return baseQuery(fetchArgs, api, extraOptions);
    }

    const release = await mutex.acquire();
    try {
      /* Запросам которым нужен токен Casdoor запрашиваем его, остальным запросам токен teachbase */
      const authData = isCasdoorTokenRequest ? await refreshCasdoorTokens() : await loginTeachbase();

      if (!authData) {
        return result;
      }

      result = await baseQuery(fetchArgs, api, extraOptions);
    } catch {
      logoutUser();
    } finally {
      release();
    }

    return result;
  };
